import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Observable, Subscription, firstValueFrom, map, of } from 'rxjs';

import { ResolveLibModule } from 'resolve-lib';
import {
  AggregatedRDataSpecialtyPracticeViewModel,
  PracticeUserViewModel,
  RegionType,
} from '../../api/models';
import { Region } from '../../api/models/region';
import { RDataService as SpecialtyService } from '../../api/services';
import { BonusType } from '../../models/bonus-type';
import { MenuOption, OptionGroup } from '../../models/menu-option';
import { RadioInput } from '../../models/radio-input';
import { DisplayCurrencyPipe } from '../../pipes/display-currency/display-currency.pipe';
import { RDataService } from '../../services/rdata.service';
import { GroupedSelectMenuComponent } from '../grouped-select-menu/grouped-select-menu.component';
import { PageWellComponent } from '../page-well/page-well.component';
import { NoAccessComponent } from '../no-access/no-access.component';
import { UserService } from '../../services/user.service';

@Component({
    selector: 'app-rdata-aggregates',
    templateUrl: './rdata-aggregates.component.html',
    styleUrls: ['./rdata-aggregates.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        PageWellComponent,
        GroupedSelectMenuComponent,
        ResolveLibModule,
        DisplayCurrencyPipe,
        NoAccessComponent
    ]
})
export class RDataAggregatesComponent implements OnInit, OnDestroy {
  #dataSubscriptions: Subscription = new Subscription();
  #defaultBonusType: BonusType = BonusType.Wrvu;

  rData$: Observable<AggregatedRDataSpecialtyPracticeViewModel>;
  userSpecialties$: Observable<OptionGroup[]> | undefined;
  userRegions$: Observable<OptionGroup[]> | undefined;
  allowedRegionOptions$: Observable<MenuOption[]>;
  userLoaded: Promise<boolean>;

  disabledSpecialties$: Observable<OptionGroup[]> | undefined;
  disabledRegions$: Observable<OptionGroup[]> | undefined;

  specialty: MenuOption | null = null;
  region: MenuOption | null = null;
  productionBonus: UntypedFormControl = new UntypedFormControl(
    this.#defaultBonusType
  );

  productionBonusOptions: RadioInput[];

  hasAccess = computed<boolean>(() => this.userService.hasAccess());

  constructor(
    private rDataService: RDataService,
    private specialtyService: SpecialtyService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.getUserSpecialtiesAndRegions();
    this.productionBonusOptions = this.#getProductionBonusTypes();
  }

  getCompensationData() {
    const specialty = this.specialty?.value as string;
    const region = this.region?.value as Region;

    this.rData$ = this.rDataService.getRDataAggregatesResponse({
      specialty: specialty,
      region: region,
    }).pipe(
      map(response => {
        if(response.ok){
          return response.body;
        }
        else if(response.status === 403){
          this.userService.hasAccess.set(false);
          return null;
        }
        else {
          return null;
        }
      })
    );
  }

  getUserSpecialtiesAndRegions() {
    const userDetails$ = this.rDataService.getUserDetails();
    this.userLoaded = new Promise((resolve) => {
      this.#dataSubscriptions.add(
        userDetails$.subscribe(async (details: PracticeUserViewModel) => {
          if(details.isActive){          
            const specialtyGroup = {
              name: 'Specialties',
              options: (details?.specialties.sort().map((specialty) => ({
                label: specialty,
                value: specialty,
              })) ?? []) as MenuOption[],
            } as OptionGroup;
            const regionGroup = {
              name: 'Regions',
              options: (details?.regions.map((region) => ({
                label: region,
                value: region,
              })) ?? []) as MenuOption[],
            } as OptionGroup;

            const allSpecialties = (await this.getAllSpecialties()) ?? [];
            const allRegions = this.getAllRegions() ?? [];

            const disabledSpecialties: OptionGroup = {
              name: 'Disabled Specialties',
              options: allSpecialties
                .filter(
                  (s: string) =>
                    !specialtyGroup.options.some((o) => o.value === s)
                )
                .map((s: string) => ({ label: s, value: s } as MenuOption)),
            };
            const disabledRegions: OptionGroup = {
              name: 'Disabled Regions',
              options: allRegions
                .filter(
                  (r: string) => !regionGroup.options.some((o) => o.value === r)
                )
                .map((r: string) => ({ label: r, value: r } as MenuOption)),
            };

            this.userSpecialties$ = of([specialtyGroup]);
            this.userRegions$ = of([regionGroup]);
            this.disabledSpecialties$ = of([disabledSpecialties]);
            this.disabledRegions$ = of([disabledRegions]);

            this.specialty = specialtyGroup.options[0] ?? null;

            this.getCompensationData();

            resolve(true);
          }
          else {
            this.userService.hasAccess.set(false);

            resolve(true);
          }
        })
      );
    });
  }

  selectSpecialty(specialtyOption: MenuOption) {
    this.specialty = specialtyOption ?? null;
    this.getCompensationData();
  }

  selectRegionOption(regionOption: MenuOption) {
    this.region = regionOption ?? null;
    this.getCompensationData();
  }

  parseStats(stat) {
    return stat ?? '-';
  }

  parsePto(stat): string {
    if(stat === null){
      return '-';
    }

    return Math.round(stat / 40).toString() ?? '-';
  }

  ngOnDestroy() {
    this.#dataSubscriptions.unsubscribe();
  }

  #getProductionBonusTypes(): RadioInput[] {
    return [
      {
        label: 'Work RVU',
        value: 'Wrvu',
        name: 'bonusType',
        id: 'Wrvu',
      },
      {
        label: 'Total Collections',
        value: 'Collections',
        id: 'Collections',
        name: 'bonusType',
      },
    ];
  }

  async getAllSpecialties(): Promise<string[]> {
    return await firstValueFrom(
      this.specialtyService
        .compensationApiRdataAggregatesSpecialtiesGet$Json$Response({
          'api-version': '1',
        })
        .pipe(map((response) => response.body ?? []))
    );
  }

  getAllRegions(): string[] {
    return Object.values(RegionType);
  }
}
